/**
 * The bloodline — the flip signature moment (Build Plan 01 P2, CANVAS §5).
 *
 * When a producer publishes a pack, the chain of custody the buyer receives is
 * minted and *shown*. The awe lives in the consequence of the producer's action
 * (ROOMS §2), not in a spectacle: this draws the real `asset_lineage` chain that
 * `@latimer-woods-tech/provenance` resolved server-side. Nothing here is decorative.
 *
 * Materials (CANVAS §2/§4): the source is amethyst, the thread and the pack are
 * gold. The chain reads root-first, top to bottom — the way custody travels.
 *
 * An original with no derivations is not a failure case. It IS the root, and the
 * copy says so: everything cut from it downstream will carry this node.
 *
 * Motion is `motion-safe:` only. Every element's resting style is its final
 * style, so with `prefers-reduced-motion` the bloodline simply *is* — no
 * animation plays and nothing is left mid-transition.
 */
import { useI18n } from '../i18n';
import type { LineageNode, ResolvedLineage } from '../lib/types';

/** Stagger between node reveals. Kept under the CANVAS §4 ceiling for a sequence. */
const STAGGER_MS = 90;

interface NodeProps {
  node: LineageNode;
  index: number;
  isRoot: boolean;
  isTarget: boolean;
  isLast: boolean;
}

function Node({ node, index, isRoot, isTarget, isLast }: NodeProps) {
  const { t } = useI18n();
  const delay = `${index * STAGGER_MS}ms`;

  return (
    <li className="relative flex gap-4 motion-safe:animate-node-in" style={{ animationDelay: delay }}>
      {/* The rail: a gold thread from this node down to the next. */}
      <div className="relative flex w-3 shrink-0 justify-center" aria-hidden>
        <span
          className={[
            'mt-1.5 h-3 w-3 shrink-0 rounded-sm border',
            isRoot ? 'border-amethyst bg-amethyst' : 'border-accent bg-accent',
            isTarget ? 'shadow-[0_0_0_4px_var(--accent-glow)]' : '',
          ].join(' ')}
        />
      </div>

      <div className={`min-w-0 grow ${isLast ? '' : 'pb-6'}`}>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={`type-body-sm truncate ${isTarget ? 'text-accent font-bold' : 'text-primary'}`}>{node.title}</span>
          {isRoot ? <span className="type-label text-amethyst-lite">{t.bloodline.source}</span> : null}
          {isTarget ? <span className="type-label text-accent">{t.bloodline.thisPack}</span> : null}
        </div>
        <p className="type-caption text-muted">
          {node.kind.replace('_', ' ')}
          {node.relation_to_child ? ` · ${node.relation_to_child}` : ''}
        </p>
      </div>
    </li>
  );
}

/**
 * Renders a resolved chain of custody. Pure: the caller fetches.
 * `chain` is root-first and always contains at least the target itself.
 */
export function Bloodline({ lineage }: { lineage: ResolvedLineage }) {
  const { t } = useI18n();
  const chain = lineage.chain;
  const rootIds = new Set(lineage.root_asset_ids);

  return (
    <div data-testid="bloodline">
      <p className="type-label text-accent">{t.bloodline.minted}</p>
      <p className="type-body text-secondary mt-2 max-w-prose">
        {lineage.is_derived ? t.bloodline.derivedBody(lineage.depth) : t.bloodline.sourceBody}
      </p>

      <ol className="relative mt-6 flex flex-col">
        {/* One continuous thread behind the nodes: amethyst at the source, gold at the pack. */}
        {chain.length > 1 ? (
          <span
            className="from-amethyst to-accent absolute bottom-2 left-1.5 top-3 w-px origin-top bg-linear-to-b motion-safe:animate-thread-draw"
            aria-hidden
          />
        ) : null}
        {chain.map((node, i) => (
          <Node
            key={node.asset_id}
            node={node}
            index={i}
            isRoot={rootIds.has(node.asset_id)}
            isTarget={node.asset_id === lineage.target_asset_id}
            isLast={i === chain.length - 1}
          />
        ))}
      </ol>
    </div>
  );
}
